<template>
  <div
    ref="dropZoneRef"
    class="z-100 flex h-screen w-full flex-col bg-gray-900"
    :class="{ 'ring-opacity-50 ring-4 ring-blue-500': isOverDropZone }">
    <!-- Drag overlay -->

    <FileDropOverlay :isVisible="isOverDropZone" />

    <!-- Editor UI -->
    <div class="flex h-full flex-col">
      <!-- Top toolbar -->

      <Header />

      <!-- Main content area -->
      <div class="relative flex flex-grow flex-col overflow-hidden">
        <ProgressIndicator />
        <div ref="canvasContainer" class="relative flex-grow overflow-hidden bg-gray-700"></div>

        <div
          ref="leftPanel"
          class="absolute top-0 bottom-0 left-0 z-10 flex flex-col bg-gray-900 shadow-lg"
          :style="{ width: appStore.leftSettingPanelWidth + 'px' }">
          <div v-if="!loading" class="h-full w-full overflow-x-hidden overflow-y-auto bg-gray-900">
            <LeftSettingPanel :collapsed="collapsedLeftPanel" />
          </div>
          <div
            v-if="!collapsedLeftPanel && appStore.leftSettingPanelWidth > PANEL_WIDTH.LEFT_MAX"
            class="absolute right-0 h-full w-1.5 cursor-ew-resize bg-transparent hover:bg-[#7DC3C0]"
            @mousedown="startLeftResize"></div>

          <div
            class="mt-auto flex h-10 items-center bg-gray-900 px-2"
            :class="[collapsedLeftPanel ? 'justify-center' : 'justify-end']">
            <PanelLeftOpen
              size="20"
              stroke-width="1.8"
              class="cursor-pointer text-gray-400"
              :class="{
                '-rotate-180': !collapsedLeftPanel,
              }"
              @click="resizeLeftPanel" />
          </div>
        </div>

        <div
          ref="rightPanel"
          class="absolute top-0 right-0 bottom-0 z-50 flex bg-gray-800"
          :style="{ width: appStore.rightSettingPanelWidth + 'px' }">
          <div
            v-if="appStore.rightSettingPanelWidth > PANEL_WIDTH.RIGHT_MIN"
            class="absolute z-12 h-full w-1.5 cursor-ew-resize bg-transparent transition-colors hover:bg-[#7DC3C0]"
            @mousedown="startRightResize"></div>

          <RightSettingPanel v-if="!loading" class="z-11" :canvasContainer="canvasContainer" />
        </div>

        <SelectedFloatingTopPanel
          :leftSidebarWidth="appStore.leftSettingPanelWidth"
          :rightSidebarWidth="appStore.rightSettingPanelWidth" />
      </div>

      <!-- Bottom panel -->
      <div class="relative flex-col">
        <!-- Resize handle -->
        <div
          class="absolute top-0 z-12 h-1.5 w-full cursor-ns-resize bg-transparent transition-colors hover:bg-[#7DC3C0]"
          @mousedown="startResize"></div>

        <!-- Panel content -->

        <div
          v-if="isDevEnvironment"
          ref="bottomPanel"
          class="relative z-11 overflow-hidden border-t border-gray-700 bg-gray-800"
          :style="{ height: bottomPanelHeight + 'px' }">
          <TimelinePanel :opened="bottomPanelHeight > 30" @toggleTimelinePanel="toggleTimeline()" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAssetStore } from '@/stores/api/asset';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';

import { cleanupScene, hasSceneLoaded, initScene, renderer, updateRendererSize } from '@/threejs/core';
import { handleDesignChanges } from '@/threejs/objectManager';

import FileDropOverlay from '@/components/common/FileDropOverlay.vue';
import Header from '@/components/layout/Header.vue';
import SelectedFloatingTopPanel from '@/components/layout/SelectedFloatingTopPanel.vue';
import TimelinePanel from '@/components/layout/TimelinePanel.vue';
import RightSettingPanel from '@/components/panels/RightSettingPanel.vue';

import { useComments } from '@/composables/useComments';
import { useFileDropZone } from '@/composables/useFileDropZone';
import { useHistoryTracker } from '@/composables/useHistoryTracker';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import { useWebsockets } from '@/composables/useWebsockets';
import { useSelectionHandler } from '@/threejs/handlers/selection';
import eventBus from '@/utils/eventBus';

import ProgressIndicator from '@/components/misc/ProgressIndicator.vue';
import LeftSettingPanel from '@/components/panels/LeftSettingPanel.vue';
import { PANEL_WIDTH } from '@/constants/settingPanels';
import { useAppStore } from '@/stores/appStore';
import { useToast } from '@disguise/cloud-ui';
import { PanelLeftOpen } from 'lucide-vue-next';

const route = useRoute();
const assetStore = useAssetStore();
const cruxModuleStore = useCruxModuleStore();
const selectionStore = useSelectionStore();
const appStore = useAppStore();
const toast = useToast();

const canvasContainer = ref(null);
const loading = ref(true);
const error = ref(null);
const cleanupSelectionEvents = ref(null);

const uploadFile = async (files) => {
  let index = 0;
  let successCount = 0;
  let errorCount = 0;
  try {
    for (const file of files) {
      try {
        index++;

        appStore.setProgressDisplayText(`Uploading ${index}/${files.length}`);
        const asset = await assetStore.uploadFile(file);

        if (asset && asset.code.startsWith('TDV')) {
          cruxModuleStore.addSceneModule(null, 'model', {
            name: asset.name,
            resource_id: asset.asset,
            ext: asset.extension,
            visible: true,
          });
          successCount++;
        } else {
          console.warn('Asset uploaded but not added to scene (type:', asset, ')');
          successCount++;
        }
      } catch (fileError) {
        console.error(`Error uploading file ${file.name}:`, fileError);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.add({
        severity: 'success',
        summary: 'Upload Complete',
        detail: `Successfully uploaded ${successCount} ${successCount === 1 ? 'file' : 'files'}`,
        life: 4000,
      });
    }

    if (errorCount > 0 && successCount === 0) {
    } else if (errorCount > 0) {
      toast.add({
        severity: 'warn',
        summary: 'Partial Upload',
        detail: `${errorCount} ${errorCount === 1 ? 'file' : 'files'} failed to upload`,
        life: 5000,
      });
    }
  } catch (error) {
    console.error('Error uploading files:', error);
    toast.add({
      severity: 'error',
      summary: 'Upload Error',
      detail: 'An unexpected error occurred during upload',
      life: 5000,
    });
  }
};
// Handle file drop function
const handleFileDrop = async (files) => {
  if (!files || files.length === 0) return;
  const fileCount = files.length;
  const fileWord = fileCount === 1 ? 'file' : 'files';

  try {
    const response = toast.promise(async () => await uploadFile(files), {
      loading_severity: 'loading',
      loading_message: `Uploading ${fileCount} ${fileWord}...`,
      loading_detail: ``,
      success_severity: 'success',
      success_message: `Upload Complete`,
      success_detail: `Successfully uploaded ${fileCount} ${fileWord}`,
      success_toast: true,
    });
  } catch (err) {
    console.error('Error in handleFileDrop:', err);
    toast.add({
      severity: 'error',
      summary: 'Upload Error',
      detail: 'An unexpected error occurred during upload',
      life: 5000,
    });
  }
};

// Setup file drop zone using composable
const { dropZoneRef, isOverDropZone, setupDropZone } = useFileDropZone(handleFileDrop, {
  multiple: true,
  // No file type restrictions - allow all file types like images, videos, and 3D models
});

const { connectWebsockets, cleanup: cleanupWebsockets } = useWebsockets();
const { setupSelectionEvents } = useSelectionHandler();
const { openEditCommentDialog } = useComments();

// Drop zone cleanup function
let cleanupDropZone = null;

useKeyboardShortcuts();
//useTimelineLogic()
useHistoryTracker();

// Add these new refs for bottom panel
const bottomPanelHeight = ref(30);
const leftPanelWidth = ref(250);
const isResizing = ref(false);

onMounted(async () => {
  hasSceneLoaded.value = false;
  loadData();

  // Setup drop zone
  cleanupDropZone = await setupDropZone();

  // Listen for edit annotation events
  eventBus.on('edit-annotation', (annotationCode) => {
    openEditCommentDialog(annotationCode);
  });

  // Initialize Three.js after next tick to ensure container is ready
  await nextTick();
  if (canvasContainer.value && !hasSceneLoaded.value) {
    initThreeJs();
  }
});

// Watch for changes to the canvasContainer
watch(canvasContainer, (newContainer) => {
  if (newContainer && !hasSceneLoaded.value) {
    initThreeJs();
  }
});

// Watch for route parameter changes to reload scene when navigating to different files
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      // Clean up the current scene before loading new one
      if (hasSceneLoaded.value) {
        cleanupThreeJs();
      }

      // Reset loading state
      hasSceneLoaded.value = false;
      loading.value = true;

      // Load new data
      await loadData();

      // Reinitialize Three.js
      await nextTick();
      if (canvasContainer.value && !hasSceneLoaded.value) {
        initThreeJs();
      }
    }
  }
);

onBeforeUnmount(() => {
  cleanupWebsockets();
  cleanupThreeJs();

  // Cleanup event bus listener
  eventBus.off('edit-annotation');

  // Cleanup drop zone
  if (cleanupDropZone) {
    cleanupDropZone();
  }

  // Ensure hasSceneLoaded is reset
  hasSceneLoaded.value = false;

  cleanupScene();
  cruxModuleStore.clearStore();
  selectionStore.clearSelection();
});

const loadData = async () => {
  try {
    // Clear the crux modules before loading new data
    // This ensures that when new modules are fetched, they're detected as "added"
    cruxModuleStore.cruxModules = [];
    cruxModuleStore.clear();

    // Load design data using current route parameter
    const currentSceneId = route.params.id;
    if (currentSceneId) {
      const asset = await assetStore.fetch(currentSceneId);
      assetStore.currentAsset = asset;
    }

    if (assetStore.currentAsset) {
      const modules = await cruxModuleStore.fetchforAsset(assetStore.currentAsset.code);
    }

    // Wait for the next DOM update cycle to ensure canvasContainer is mounted
    await nextTick();

    connectWebsockets();

    loading.value = false;
  } catch (err) {
    console.error('Error initializing editor:', err);
    error.value = 'Failed to load the 3D editor. Please try again.';
    loading.value = false;
  }
};

const initThreeJs = async () => {
  if (!canvasContainer.value) {
    console.error('Canvas container not found');
    return;
  }

  await nextTick();

  // Check if container has dimensions
  const { clientWidth, clientHeight } = canvasContainer.value;
  if (clientWidth === 0 || clientHeight === 0) {
    console.error('Canvas container has zero dimensions', {
      clientWidth,
      clientHeight,
    });

    return;
  }

  // Initialize the Three.js scene with our container element
  const { scene, camera, cameraControls } = initScene(canvasContainer.value);

  // Set up selection event listeners and store cleanup function
  cleanupSelectionEvents.value = setupSelectionEvents(renderer, camera, scene, cameraControls);

  hasSceneLoaded.value = true;

  // Manually trigger scene population with loaded modules
  // Since modules were loaded before hasSceneLoaded was true,
  // the history watcher wouldn't have processed them
  if (cruxModuleStore.cruxModules.length > 0) {
    handleDesignChanges([], cruxModuleStore.cruxModules);
  }
};

const cleanupThreeJs = () => {
  // Remove the renderer from the DOM first
  if (canvasContainer.value && renderer.domElement.parentNode === canvasContainer.value) {
    canvasContainer.value.removeChild(renderer.domElement);
  }

  // Clean up selection event listeners
  if (cleanupSelectionEvents.value) {
    cleanupSelectionEvents.value();
    cleanupSelectionEvents.value = null;
  }

  // Clean up Three.js resources
  cleanupScene();
};

// Add resize handler functions
const startResize = (e) => {
  isResizing.value = true;
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', handleResize);
  document.addEventListener('mouseup', stopResize);
  document.addEventListener('mouseleave', stopResize);
};

const startLeftResize = (e) => {
  isResizing.value = true;
  // stop highlighing text
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', handleLeftResize);
  document.addEventListener('mouseup', stopResize);
  document.addEventListener('mouseleave', stopResize);
};

const startRightResize = (e) => {
  isResizing.value = true;
  // stop highlighing text
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', handleRightResize);
  document.addEventListener('mouseup', stopResize);
  document.addEventListener('mouseleave', stopResize);
};

const handleResize = (e) => {
  if (!isResizing.value) return;

  const containerHeight = window.innerHeight;
  const newHeight = containerHeight - e.clientY;
  const minHeight = 100;
  const maxHeight = containerHeight * 0.5;

  if (newHeight >= minHeight && newHeight <= maxHeight) {
    bottomPanelHeight.value = newHeight;
  }
  updateRendererSize(canvasContainer.value);
};

const toggleTimeline = () => {
  if (bottomPanelHeight.value > 30) {
    bottomPanelHeight.value = 30;
  } else {
    bottomPanelHeight.value = 150;
  }
  updateRendererSize(canvasContainer.value);
};

const handleLeftResize = (e) => {
  if (!isResizing.value) return;

  const newWidth = e.clientX;
  const minWidth = PANEL_WIDTH.LEFT_MAX;
  // Initial value was 500 but for now make same as max but based on feature request we can update it.
  const maxWidth = PANEL_WIDTH.LEFT_MAX;

  if (newWidth >= minWidth && newWidth <= maxWidth) {
    appStore.setLeftSettingPanelWidth(newWidth);
  }
};

const handleRightResize = (e) => {
  if (!isResizing.value) return;

  const containerWidth = window.innerWidth;
  const newWidth = containerWidth - e.clientX;
  const minWidth = PANEL_WIDTH.RIGHT_MAX;
  const maxWidth = containerWidth - 500;

  if (newWidth >= minWidth && newWidth <= maxWidth) {
    appStore.setRightSettingPanelWidth(newWidth);
  }
};

const stopResize = () => {
  isResizing.value = false;
  document.body.style.userSelect = 'auto';
  document.removeEventListener('mousemove', handleResize);
  document.removeEventListener('mousemove', handleLeftResize);
  document.removeEventListener('mousemove', handleRightResize);
  document.removeEventListener('mouseup', stopResize);
  document.removeEventListener('mouseleave', stopResize);
};

const collapsedLeftPanel = computed(() => appStore.leftSettingPanelWidth === PANEL_WIDTH.LEFT_MIN);

const resizeLeftPanel = () => {
  appStore.setLeftSettingPanelWidth(collapsedLeftPanel.value ? PANEL_WIDTH.LEFT_MAX : PANEL_WIDTH.LEFT_MIN);
};

const isDevEnvironment = computed(() => {
  return import.meta.env.VITE_USER_NODE_ENV === 'development';
});
</script>
